/**
 * Shared AD relationship constants for default queries.
 *
 * These relationship arrays define the edge types used in AD attack path queries.
 * Import and spread these into your query's `label` field.
 */

/**
 * AD Pathfinding Edges - Complete set matching BloodHound's ActiveDirectoryPathfindingEdges()
 *
 * This list defines all relationship types used for privilege escalation and lateral movement
 * in Active Directory attack path analysis. It matches BloodHound's adTransitEdgeTypes exactly.
 *
 * Source: BloodHound's ActiveDirectoryPathfindingEdges() function
 * Backend: Verified against tabularium/pkg/model/model/ad_relationship.go (ADRelationshipLabels)
 */
export const AD_PATHFINDING_RELATIONSHIPS = [
  'Owns',
  'GenericAll',
  'GenericWrite',
  'WriteOwner',
  'WriteDacl',
  'MemberOf',
  'ForceChangePassword',
  'AllExtendedRights',
  'AddMember',
  'HasSession',
  'GPLink',
  'AllowedToDelegate',
  'CoerceToTGT',
  'AllowedToAct',
  'AdminTo',
  'CanPSRemote',
  'CanRDP',
  'ExecuteDCOM',
  'HasSIDHistory',
  'AddSelf',
  'DCSync',
  'ReadLAPSPassword',
  'ReadGMSAPassword',
  'DumpSMSAPassword',
  'SQLAdmin',
  'AddAllowedToAct',
  'WriteSPN',
  'AddKeyCredentialLink',
  'SyncLAPSPassword',
  'WriteAccountRestrictions',
  'WriteGPLink',
  'GoldenCert',
  'ADCSESC1',
  'ADCSESC3',
  'ADCSESC4',
  'ADCSESC6a',
  'ADCSESC6b',
  'ADCSESC9a',
  'ADCSESC9b',
  'ADCSESC10a',
  'ADCSESC10b',
  'ADCSESC13',
  'SyncedToEntraUser',
  'CoerceAndRelayNTLMToSMB',
  'CoerceAndRelayNTLMToADCS',
  'WriteOwnerLimitedRights',
  'OwnsLimitedRights',
  'ClaimSpecialIdentity',
  'CoerceAndRelayNTLMToLDAP',
  'CoerceAndRelayNTLMToLDAPS',
  'ContainsIdentity',
  'PropagatesACEsTo',
  'GPOAppliesTo',
  'CanApplyGPO',
  'HasTrustKeys',
  'ManageCA',
  'ManageCertificates',
  'Contains',
  'DCFor',
  'SameForestTrust',
  'SpoofSIDHistory',
  'AbuseTGTDelegation',
] as const;

export const ACL_OWNERSHIP_RELATIONSHIPS = [
  'GenericAll',
  'GenericWrite',
  'WriteOwner',
  'WriteOwnerLimitedRights',
  'WriteOwnerRaw',
  'WriteDacl',
  'Owns',
  'OwnsLimitedRights',
  'OwnsRaw',
  'AllExtendedRights',
  'AddMember',
  'ForceChangePassword',
  'AddAllowedToAct',
  'AllowedToAct',
  'AddSelf',
  'AddKeyCredentialLink',
  'WriteAccountRestrictions',
  'WriteGPLink',
  'ManageCertificates',
  'ManageCA',
  'DelegatedEnrollmentAgent',
  'Enroll',
  'HostsCAService',
  'WritePKIEnrollmentFlag',
  'WritePKINameFlag',
  'GoldenCert',
] as const;

export const RDP_ACCESS_RELATIONSHIPS = [
  'CanRDP',
  'CanPSRemote',
  'ExecuteDCOM',
  'MemberOf',
] as const;

export const DOMAIN_USERS_DIRECT_PERMISSIONS_RELATIONSHIPS = [
  'GenericAll',
  'GenericWrite',
  'WriteOwner',
  'WriteDacl',
  'Owns',
  'AdminTo',
  'CanRDP',
  'CanPSRemote',
  'ExecuteDCOM',
  'AllExtendedRights',
  'ForceChangePassword',
  'AddMember',
  'WriteGPLink',
  'WriteAccountRestrictions',
] as const;
